import { derived, type Readable, writable } from 'svelte/store';
import { goto } from '$app/navigation';
import { navigating, page } from '$app/stores';
import {
	isAuthenticatedPage,
	isUnauthenticatedPage,
	type RouteName,
} from '$router';
import {
	BaseViewModel,
	type BaseViewModelInterface,
	type BaseViewModelOptions,
} from '$services';
import type { CurrentUser, UserSessionData } from '@localekey/types';
import type { AppLoadingData } from '@shared/frontend/services';
import { isInIframe } from '@shared/frontend/utils';
import type { DeviceData } from '@shared/types';

type AppViewModelOptions = BaseViewModelOptions & {
	device: DeviceData | undefined;
	userSession: UserSessionData | undefined;
	currentRoutePath: RouteName | undefined;
};

export type AppViewModelInterface = {
	readonly initialized: Readable<boolean>;

	readonly showAppBar: Readable<boolean>;
	readonly isLoggedIn: Readable<boolean>;
	handlePopStateEvent(event: PopStateEvent): Promise<void>;

	handleAppClose(event: BeforeUnloadEvent): void;
} & BaseViewModelInterface;
class AppViewModel extends BaseViewModel implements AppViewModelInterface {
	readonly initialized = writable(false);
	readonly isNavigationDrawerMinified = writable(false);
	readonly navigationDrawerOffset = writable(0);

	private _currentTeamId: string | undefined;
	private _lastTimeNavigated: number | undefined;
	constructor(options: AppViewModelOptions) {
		super(options);
		const { currentRoutePath, device, userSession } = options;
		void this.execute('authService', 'setCurrentUser', userSession, true);
		void this.execute('appService', 'setCurrentDevice', device);

		if (currentRoutePath) {
			void this.execute(
				'routerService',
				'setCurrentPage',
				currentRoutePath,
			);
		}
	}

	async handlePopStateEvent(event: PopStateEvent): Promise<void> {
		await this.execute('i18nService', 'handlePopStateEvent', event);
	}

	get appLoading(): Readable<AppLoadingData | undefined> {
		return this.getR('dialogService', 'appLoading', undefined);
	}

	get showAppBar(): Readable<boolean> {
		return derived(
			[this._currentRoute, this.isLoggedIn],
			([$currentRoute, $isLoggedIn]) => {
				return !this._isMinimalRouteView($currentRoute, $isLoggedIn);
			},
		);
	}

	get isLoggedIn(): Readable<boolean> {
		return this.getR('authService', 'isLoggedIn', false);
	}

	/** Initialize the view model and set up necessary bindings. */
	override async initialize(): Promise<void> {
		this.isNavigationDrawerMinified.set(this._isInIframe);
		await this._setupURLBasedActions();
		await this._initializeServices();
		this._subscribeToAppState();
		this.initialized.set(true);
		await this._lazyLoadServices();
	}

	override async dispose(): Promise<void> {
		this.log('dispose');
		return super.dispose();
	}

	handleAppClose(event: BeforeUnloadEvent): void {
		this.log('handleAppClose', event);
	}

	/** Subscribe to application state changes. */
	private _subscribeToAppState() {
		this.subscribe(
			this.getR('remoteConfigService', 'logLevel', undefined),
			async (logLevel) => {
				if (logLevel) {
					await this.execute('appService', 'setLogLevel', logLevel);
				}
			},
		);

		this.subscribe(
			derived(
				[
					this.getR('authService', 'currentUser', undefined),
					this.getR('authService', 'isChangingAuthState', false),
					this.getR('routerService', 'isNavigating', false),
					this._currentRoute,
					this.getR('authService', 'isFrontendLoggedIn', false),
				],
				([
					currentUser,
					isChangingAuthState,
					isNavigating,
					currentRoute,
					isFrontendLoggedIn,
				]) => ({
					currentRoute,
					currentUser,
					isChangingAuthState,
					isFrontendLoggedIn,
					isNavigating,
				}),
			),
			async (options) => {
				const { isChangingAuthState, isNavigating } = options;

				if (isChangingAuthState || isNavigating) {
					return;
				}
				this.log('auth/route changed', options);

				await Promise.all([
					this._handleRouteTransitions(options),
					this._handleAuthStateChanges(options),
				]);
			},
		);
	}

	/** Initialize services required by the app. */
	private async _initializeServices() {
		await Promise.all([
			this.execute('routerService', 'initialize', {
				goto,
				navigating,
				page,
			}),
			// this.execute('appService', 'initialize'),
			this.execute('authService', 'initialize'),
		]);
	}

	/**
	 * Handle transitions between routes and ensure correct UI state.
	 *
	 * @param options - Options.
	 */
	private async _handleRouteTransitions(options: {
		currentUser?: CurrentUser;
		currentRoute?: string;
	}): Promise<void> {
		const { currentRoute, currentUser } = options;

		if (!currentRoute) {
			return;
		}

		const hasJustNavigated = () => {
			/** 5 seconds */
			const navigateThreshold = 5000;

			const now = Date.now();

			if (
				!this._lastTimeNavigated ||
				now - this._lastTimeNavigated > navigateThreshold
			) {
				this._lastTimeNavigated = now;
				return false;
			}

			this.log('hasJustNavigated', {
				lastTimeNavigated: this._lastTimeNavigated,
				now,
			});
			this._lastTimeNavigated = now;
			return true;
		};

		if (
			currentUser?.userRole &&
			isUnauthenticatedPage(currentRoute) &&
			currentRoute !== 'register' // we don't want to redirect when crm registration is in progress, we want to show success screen
		) {
			if (hasJustNavigated()) {
				return;
			}

			this.log('navigateToApp', {
				currentRoute,
				currentUser,
			});
			// await this.execute('authService', 'syncAuthWithBackend');

			return this.execute('routerService', 'navigateToApp');
		}

		if (!currentUser?.userRole && isAuthenticatedPage(currentRoute)) {
			if (hasJustNavigated()) {
				return;
			}

			this.log('navigateToAuth', {
				currentRoute,
				currentUser,
			});
			// await this.execute('authService', 'syncAuthWithBackend');

			return this.goToRoute({
				pathParameters: undefined,
				queryParameters: undefined,
				route: currentUser?.userRole ? 'register' : 'login',
				setRedirectTo: true,
			});
		}
	}

	/**
	 * Handle changes in authentication state and update UI and services
	 * accordingly.
	 *
	 * @param options - Options.
	 */
	private async _handleAuthStateChanges(options: {
		currentUser?: CurrentUser;
		isFrontendLoggedIn: boolean;
		currentRoute: RouteName | undefined;
	}): Promise<void> {
		const { currentUser } = options;

		const teamId = currentUser?.currentTeam?.id;

		if (this._currentTeamId !== teamId) {
			this._currentTeamId = teamId;
			await this.execute('teamService', 'setCurrentTeamId', teamId);
		}

		await Promise.all([
			// this.execute('userStatsService', 'listenForUserStats', uid),
		]);
	}

	/**
	 * Set up URL-based actions and handle initial URL parameters.
	 *
	 * @private
	 */
	async _setupURLBasedActions(): Promise<void> {
		const url = new URL(window.location.href);
		const { searchParams } = url;

		if (searchParams.get('joined')) {
			searchParams.delete('joined');
			await this.showSnackbar({
				text: this.$t.team_join_success(),
			});
			await goto(url.href, {
				keepFocus: true,
				noScroll: true,
				replaceState: true,
			});
		}

		if (searchParams.get('open-invite-dialog')) {
			searchParams.delete('open-invite-dialog');
			await this.execute(
				'appService',
				'setShowInviteMembersDialog',
				true,
			);

			await goto(url.href, {
				keepFocus: true,
				noScroll: true,
				replaceState: true,
			});
		}
	}

	private get _isInIframe(): boolean {
		return isInIframe();
	}

	/**
	 * Lazily load services that are not immediately required for the initial
	 * render.
	 */
	private async _lazyLoadServices(): Promise<void> {
		try {
			await Promise.all([
				this.execute('remoteConfigService', 'initialize'),
				this.execute('preferenceService', 'initialize'),
			]);
		} catch (error) {
			this.error('_lazyLoadServices', error);
		}
	}

	private _isMinimalRouteView(
		route?: RouteName,
		isLoggedIn?: boolean,
	): boolean {
		if (!route) {
			return false;
		}

		if (route === 'register' && isLoggedIn) {
			return true;
		}

		const minimalRoutes: Readonly<RouteName[]> = ['authenticate'] as const;

		return minimalRoutes.includes(route);
	}

	private get _currentRoute(): Readable<RouteName | undefined> {
		return this.getR('routerService', 'currentRoute', undefined);
	}
}

export const getAppViewModel = (
	options: Omit<AppViewModelOptions, 'className'>,
): AppViewModelInterface =>
	new AppViewModel({
		className: 'AppViewModel',
		...options,
	});
